"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.BatchColumnEncoder = void 0;
const tslib_1 = require("tslib");
const json_crdt_patch_1 = require("../../../json-crdt-patch");
const util_1 = require("../util");
const operations = tslib_1.__importStar(require("../../../json-crdt-patch/operations"));
class BatchColumnEncoder {
    constructor() {
        // ------------------------------------------------------ Header - page stats
        this.minSeq = 0;
        this.maxSeq = 0;
        this.sids = new Set();
        this.sidTable = [];
        this.sidMap = new Map();
        // ------------------------------------------------------------------ Columns
        /**
         * Unsigned integers - used for multiple purposes:
         *
         * - Group size - number of operations in group
         * - Operation size - number of data elements in operation
         * - Opcodes - type of each operation in the group
         */
        this.uint = [];
        /** SID old. */
        this.s_old = [];
        /** SID new. */
        this.s_new = [];
        /** Time for existing obj references (obj, ref). */
        this.t_obj = [];
        /** Time for operation IDs. */
        this.t_id = [];
        /** Time for operation values. */
        this.t_val = [];
        /** JSON data encoded as CBOR. */
        this.data = [];
        this.meta = [];
    }
    build(batch) {
        const patches = batch.patches;
        const length = patches.length;
        this.meta.push(batch.meta ?? null);
        this.uint.push(length);
        for (let i = 0; i < length; i++)
            this.buildGroup(patches[i]);
        const { sidMap, s_old: osid, s_new: nsid } = this;
        this.sidTable = [...this.sids];
        this.sids.clear();
        this.sidTable.sort();
        const sidTableLength = this.sidTable.length;
        for (let i = 0; i < sidTableLength; i++)
            sidMap.set(this.sidTable[i], i);
        const osidLength = osid.length;
        for (let i = 0; i < osidLength; i++)
            osid[i] = sidMap.get(osid[i]);
        const nsidLength = nsid.length;
        for (let i = 0; i < nsidLength; i++)
            nsid[i] = sidMap.get(nsid[i]);
        this.s_old = (0, util_1.ze)((0, util_1.rle)(this.s_old));
        this.s_new = (0, util_1.ze)((0, util_1.rle)(this.s_new));
        this.t_obj = (0, util_1.ze)((0, util_1.rle)((0, util_1.ze)((0, util_1.de)(this.t_obj))));
        this.t_id = (0, util_1.ze)((0, util_1.rle)((0, util_1.ze)((0, util_1.de)(this.t_id))));
        this.t_val = (0, util_1.ze)((0, util_1.rle)((0, util_1.ze)((0, util_1.de)(this.t_val))));
        this.meta = (0, util_1.drle)(this.meta);
    }
    buildGroup(group) {
        const { ops, meta } = group;
        const length = ops.length;
        this.uint.push(length);
        if (!length)
            return;
        this.meta.push(meta ?? null);
        for (let i = 0; i < length; i++)
            this.buildOp(ops[i]);
    }
    buildOp(op) {
        const { uint: size, uint: type, data } = this;
        this.writeId(op.id);
        switch (op.constructor) {
            case operations.NewConOp: {
                const operation = op;
                const val = operation.val;
                if (val instanceof json_crdt_patch_1.Timestamp) {
                    type.push(0 /* JsonCrdtPatchOpcodeOverlay.new_con */ + 1);
                    this.writeTsNew(val);
                }
                else {
                    type.push(0 /* JsonCrdtPatchOpcodeOverlay.new_con */);
                    data.push(val);
                }
                break;
            }
            case operations.NewValOp: {
                type.push(8 /* JsonCrdtPatchOpcodeOverlay.new_val */);
                break;
            }
            case operations.NewObjOp: {
                type.push(16 /* JsonCrdtPatchOpcodeOverlay.new_obj */);
                break;
            }
            case operations.NewVecOp: {
                type.push(24 /* JsonCrdtPatchOpcodeOverlay.new_vec */);
                break;
            }
            case operations.NewStrOp: {
                type.push(32 /* JsonCrdtPatchOpcodeOverlay.new_str */);
                break;
            }
            case operations.NewBinOp: {
                type.push(40 /* JsonCrdtPatchOpcodeOverlay.new_bin */);
                break;
            }
            case operations.NewArrOp: {
                type.push(48 /* JsonCrdtPatchOpcodeOverlay.new_arr */);
                break;
            }
            case operations.InsValOp: {
                type.push(72 /* JsonCrdtPatchOpcodeOverlay.ins_val */);
                const { obj, val } = op;
                this.writeTsOld(obj);
                this.writeTsNew(val);
                break;
            }
            case operations.InsObjOp: {
                const { obj, data: opData } = op;
                const length = opData.length;
                if (length <= 0b111) {
                    type.push(80 /* JsonCrdtPatchOpcodeOverlay.ins_obj */ + length);
                }
                else {
                    type.push(80 /* JsonCrdtPatchOpcodeOverlay.ins_obj */);
                    size.push(length);
                }
                this.writeTsOld(obj);
                for (let i = 0; i < length; i++) {
                    const tuple = opData[i];
                    data.push(tuple[0]);
                    this.writeTsNew(tuple[1]);
                }
                break;
            }
            case operations.InsVecOp: {
                const { obj, data: opData } = op;
                const length = opData.length;
                if (length <= 0b111) {
                    type.push(88 /* JsonCrdtPatchOpcodeOverlay.ins_vec */ + length);
                }
                else {
                    type.push(88 /* JsonCrdtPatchOpcodeOverlay.ins_vec */);
                    size.push(length);
                }
                this.writeTsOld(obj);
                for (let i = 0; i < length; i++) {
                    const tuple = opData[i];
                    size.push(tuple[0]);
                    this.writeTsNew(tuple[1]);
                }
                break;
            }
            case operations.InsStrOp: {
                type.push(96 /* JsonCrdtPatchOpcodeOverlay.ins_str */);
                const operation = op;
                this.writeTsOld(operation.obj);
                this.writeTsOld(operation.ref);
                data.push(operation.data);
                break;
            }
            case operations.InsBinOp: {
                type.push(104 /* JsonCrdtPatchOpcodeOverlay.ins_bin */);
                const operation = op;
                this.writeTsOld(operation.obj);
                this.writeTsOld(operation.ref);
                data.push(operation.data);
                break;
            }
            case operations.InsArrOp: {
                const operation = op;
                const { obj, ref, data: vals } = operation;
                const length = vals.length;
                if (length <= 0b111) {
                    type.push(112 /* JsonCrdtPatchOpcodeOverlay.ins_arr */ + length);
                }
                else {
                    type.push(112 /* JsonCrdtPatchOpcodeOverlay.ins_arr */);
                    size.push(length);
                }
                this.writeTsOld(obj);
                this.writeTsOld(ref);
                for (let i = 0; i < length; i++)
                    this.writeTsNew(vals[i]);
                break;
            }
            case operations.UpdArrOp: {
                type.push(120 /* JsonCrdtPatchOpcodeOverlay.upd_arr */);
                const operation = op;
                const { obj, ref, val } = operation;
                this.writeTsOld(obj);
                this.writeTsOld(ref);
                this.writeTsNew(val);
                break;
            }
            case operations.DelOp: {
                const { obj, what } = op;
                const length = what.length;
                if (length <= 0b111) {
                    type.push(128 /* JsonCrdtPatchOpcodeOverlay.del */ + length);
                }
                else {
                    type.push(128 /* JsonCrdtPatchOpcodeOverlay.del */);
                    size.push(length);
                }
                this.writeTsOld(obj);
                for (let i = 0; i < length; i++) {
                    const timespan = what[i];
                    this.writeTsOld(timespan);
                    size.push(timespan.span);
                }
                break;
            }
            case operations.NopOp: {
                const { len } = op;
                if (len <= 0b111) {
                    type.push(136 /* JsonCrdtPatchOpcodeOverlay.nop */ + len);
                }
                else {
                    type.push(136 /* JsonCrdtPatchOpcodeOverlay.nop */);
                    size.push(len);
                }
                break;
            }
            default: {
                throw new Error('UNKNOWN_OP');
            }
        }
    }
    writeId({ sid, time }) {
        this.s_new.push(sid);
        this.t_id.push(time);
        this.sids.add(sid);
        if (!this.minSeq || time < this.minSeq)
            this.minSeq = time;
        this.maxSeq = Math.max(this.maxSeq, time);
    }
    writeTsNew({ sid, time }) {
        this.s_new.push(sid);
        this.t_val.push(time);
        this.sids.add(sid);
        if (!this.minSeq || time < this.minSeq)
            this.minSeq = time;
        this.maxSeq = Math.max(this.maxSeq, time);
    }
    writeTsOld({ sid, time }) {
        this.s_old.push(sid);
        this.t_obj.push(time);
        this.sids.add(sid);
        if (!this.minSeq || time < this.minSeq)
            this.minSeq = time;
        this.maxSeq = Math.max(this.maxSeq, time);
    }
}
exports.BatchColumnEncoder = BatchColumnEncoder;
